import type { DefaultTimeframe } from '@plato/app-meta/default-timeframe';
import { SrInsightType } from '@plato/data/starrocks/types';
import type { GetManyCustomersFilters } from '../../../server/types/infer-trpc-types.ts';
import { CustomerSortBy, SortOrder } from '../../../server/types/list-view-types.ts';

export const managerOnlySortFields: CustomerSortBy[] = [
    CustomerSortBy.DOCUMENTED_VISITS,
    CustomerSortBy.VISITS_PER_100K,
    CustomerSortBy.LAST_VISIT,
];

export const defaultSortFilter: { field: CustomerSortBy; direction: SortOrder } = {
    field: CustomerSortBy.TTM,
    direction: SortOrder.DESC,
};

const timeframeSortFieldPairs = [
    [CustomerSortBy.TTM, CustomerSortBy.REVENUE_YTD],
    [CustomerSortBy.TTM_DELTA, CustomerSortBy.REVENUE_YTD_DELTA],
    [CustomerSortBy.GROSS_MARGIN_TTM, CustomerSortBy.GROSS_MARGIN_YTD],
    [CustomerSortBy.GROSS_MARGIN_TTM_DELTA, CustomerSortBy.GROSS_MARGIN_YTD_DELTA],
] as const;

function sortFieldForTimeframe(field: CustomerSortBy, timeframe: DefaultTimeframe): CustomerSortBy {
    const pair = timeframeSortFieldPairs.find(([ttm, ytd]) => ttm === field || ytd === field);

    if (!pair) {
        return field;
    }

    const [ttm, ytd] = pair;
    return timeframe === 'TTM' ? ttm : ytd;
}

export function mapSortFilterAccordingToUserPreferences(
    filters: GetManyCustomersFilters,
    userPreferences: {
        isManagerView: boolean;
        defaultTimeframe: DefaultTimeframe;
    },
): {
    field: CustomerSortBy;
    direction: SortOrder;
} {
    const { isManagerView, defaultTimeframe } = userPreferences;
    const { sortingFilter } = filters;

    // Manager-only sort fields fall back to the default sort outside Manager
    // view (their columns are not rendered there). Applied on read only — the
    // persisted value is kept, so switching back to Manager view restores it.
    const effectiveSortingFilter =
        !isManagerView && managerOnlySortFields.includes(sortingFilter.field)
            ? defaultSortFilter
            : sortingFilter;

    return {
        field: sortFieldForTimeframe(effectiveSortingFilter.field, defaultTimeframe),
        direction: effectiveSortingFilter.direction,
    };
}

export const customersExpansionPotentialFilter = {
    cityNames: [],
    zipCodes: [],
    salesRepIds: [],
    searchString: '',
    industryNames: [],
    insightTypes: [],
    statuses: [],
    companyIds: [],
    customerClasses: [],
    salesRegions: [],
    customerIds: [],
    sortingFilter: {
        field: CustomerSortBy.INSIGHTS_SUM_OPPORTUNITY,
        direction: SortOrder.DESC,
    },
} satisfies GetManyCustomersFilters;

export const customersRevenueDeclineFilter = {
    cityNames: [],
    zipCodes: [],
    salesRepIds: [],
    searchString: '',
    industryNames: [],
    // Only the category type is selected: `customerBaseQuery` widens it to cover
    // `GlobalChurnWarning` too, so one chip and one checked box stand for both.
    insightTypes: [SrInsightType.CHURN_WARNING],
    statuses: [],
    companyIds: [],
    customerClasses: [],
    salesRegions: [],
    customerIds: [],
    sortingFilter: {
        field: CustomerSortBy.INSIGHTS_SUM_RISK,
        direction: SortOrder.ASC,
    },
} satisfies GetManyCustomersFilters;
